import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import type { IClientUser } from '../../interfaces/user';
import { GET_ADMIN_CONFIGS, UPDATE_ADMIN_CONFIG, SEND_TEST_COFFEE_CHAT_EMAIL } from '../../graphql/adminConfig';
import Navigation from '../../components/Navigation';

interface AdminConfig {
  id: string;
  key: string;
  value: string;
  description?: string;
}

const AdminSettings: React.FC<IClientUser> = (props) => {
  const [autoArchiveEnabled, setAutoArchiveEnabled] = useState(false);
  const [autoArchiveDays, setAutoArchiveDays] = useState('365');
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [coffeeChatEmailEnabled, setCoffeeChatEmailEnabled] = useState(true);
  const [coffeeChatEmailLoading, setCoffeeChatEmailLoading] = useState(false);
  const [coffeeChatEmailStatus, setCoffeeChatEmailStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');
  const [testEmailError, setTestEmailError] = useState('');

  const { data, loading, error, refetch } = useQuery(GET_ADMIN_CONFIGS);
  const [updateConfig] = useMutation(UPDATE_ADMIN_CONFIG);
  const [sendTestEmail] = useMutation(SEND_TEST_COFFEE_CHAT_EMAIL);

  useEffect(() => {
    if (data?.adminConfigs) {
      const configs = data.adminConfigs as AdminConfig[];
      const enabledConfig = configs.find((c) => c.key === 'auto_archive_enabled');
      const daysConfig = configs.find((c) => c.key === 'auto_archive_after_days');

      if (enabledConfig) {
        setAutoArchiveEnabled(enabledConfig.value === 'true');
      }
      if (daysConfig) {
        setAutoArchiveDays(daysConfig.value);
      }

      const coffeeChatEmailConfig = configs.find(
        (c) => c.key === 'coffee_chat_email_notifications_enabled'
      );
      if (coffeeChatEmailConfig) {
        setCoffeeChatEmailEnabled(coffeeChatEmailConfig.value !== 'false');
      }
    }
  }, [data]);

  const handleSave = async () => {
    setIsSaving(true);
    setSuccessMessage('');

    try {
      await updateConfig({
        variables: {
          input: {
            key: 'auto_archive_enabled',
            value: autoArchiveEnabled ? 'true' : 'false'
          }
        }
      });

      await updateConfig({
        variables: {
          input: {
            key: 'auto_archive_after_days',
            value: autoArchiveDays
          }
        }
      });

      setSuccessMessage('Settings saved successfully!');
      refetch();
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCoffeeChatEmailToggle = async (newValue: boolean) => {
    setCoffeeChatEmailLoading(true);
    setCoffeeChatEmailStatus('idle');
    try {
      await updateConfig({
        variables: {
          input: {
            key: 'coffee_chat_email_notifications_enabled',
            value: newValue ? 'true' : 'false'
          }
        }
      });
      setCoffeeChatEmailEnabled(newValue);
      setCoffeeChatEmailStatus('success');
      setTimeout(() => setCoffeeChatEmailStatus('idle'), 2000);
    } catch (err) {
      console.error('Error updating coffee chat email setting:', err);
      setCoffeeChatEmailStatus('error');
      setTimeout(() => setCoffeeChatEmailStatus('idle'), 3000);
    } finally {
      setCoffeeChatEmailLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress.trim()) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmailAddress.trim())) {
      setTestEmailStatus('error');
      setTestEmailError('Please enter a valid email address.');
      setTimeout(() => setTestEmailStatus('idle'), 3000);
      return;
    }

    setTestEmailSending(true);
    setTestEmailStatus('idle');
    setTestEmailError('');

    try {
      const response = await sendTestEmail({
        variables: { email: testEmailAddress.trim() }
      });

      if (response.data.sendTestCoffeeChatEmail === true) {
        setTestEmailStatus('success');
        setTestEmailAddress('');
        setTimeout(() => setTestEmailStatus('idle'), 3000);
      } else {
        setTestEmailStatus('error');
        setTestEmailError('Failed to send test email. Check server logs.');
        setTimeout(() => setTestEmailStatus('idle'), 3000);
      }
    } catch (err) {
      console.error('Error sending test email:', err);
      setTestEmailStatus('error');
      setTestEmailError('Failed to send test email. Please try again.');
      setTimeout(() => setTestEmailStatus('idle'), 3000);
    } finally {
      setTestEmailSending(false);
    }
  };

  if (loading) {
    return (
      <main className="page-admin-settings">
        <Navigation {...props} />
        <section className="container">
          <div className="admin-users-container">
            <div className="admin-header">
              <div className="admin-header-content">
                <h1 className="admin-title">Settings</h1>
                <p className="admin-subtitle">Configure automated tasks and system settings</p>
              </div>
            </div>
            <div className="box">
              <p>Loading settings...</p>
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="page-admin-settings">
        <Navigation {...props} />
        <section className="container">
          <div className="admin-users-container">
            <div className="admin-header">
              <div className="admin-header-content">
                <h1 className="admin-title">Settings</h1>
                <p className="admin-subtitle">Configure automated tasks and system settings</p>
              </div>
            </div>
            <div className="box">
              <div className="notification is-danger">
                <p className="title is-5">Error Loading Settings</p>
                <p>{error.message}</p>
                <p className="mt-3 is-size-7">
                  Make sure the admin config GraphQL endpoints are deployed.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="page-admin-settings">
      <Navigation {...props} />
      <section className="container">
        <div className="admin-users-container">
          <div className="admin-header">
            <div className="admin-header-content">
              <h1 className="admin-title">Settings</h1>
              <p className="admin-subtitle">Configure automated tasks and system settings</p>
            </div>
          </div>

          <div className="box">
            <h2 className="title is-5">Auto-Archive Projects</h2>
            <p className="has-text-dark" style={{ marginBottom: '1.5rem' }}>
              Automatically archive projects that are older than a specified number of days.
               This cron job runs daily at midnight.
            </p>

            <div className="field">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={autoArchiveEnabled}
                  onChange={(e) => setAutoArchiveEnabled(e.target.checked)}
                />
                <span className="ml-2">Enable auto-archive</span>
              </label>
            </div>

            <div className="field">
              <label className="label">Archive projects older than (days)</label>
              <div className="control">
                <input
                  className="input"
                  type="number"
                  min="1"
                  value={autoArchiveDays}
                  onChange={(e) => setAutoArchiveDays(e.target.value)}
                  disabled={!autoArchiveEnabled}
                />
              </div>
              <p className="help">
                Default: 365 days (1 year). Projects created before this threshold will be
                archived.
              </p>
            </div>

            {successMessage && (
              <div className="notification is-success is-light">
                <button
                  className="delete"
                  onClick={() => setSuccessMessage('')}
                ></button>
                {successMessage}
              </div>
            )}

            <div className="field">
              <div className="control">
                <button
                  className={`button is-primary ${isSaving ? 'is-loading' : ''}`}
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>

          {/* ── Coffee Chat Email Notifications ── */}
          <div className="box" style={{ marginTop: '1.5rem' }}>
            <div className="settings-toggle-row">
              <div className="settings-toggle-text">
                <h2 className="title is-5" style={{ marginBottom: '0.5rem' }}>
                  Coffee Chat Email Notifications
                </h2>
                <p className="has-text-dark">
                  Send email notifications to users when a new coffee chat match
                  is made. When disabled, no match emails will be sent.
                </p>
              </div>
              <div className="settings-toggle-control">
                <label
                  className={`toggle-switch${
                    coffeeChatEmailLoading ? ' is-disabled' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={coffeeChatEmailEnabled}
                    onChange={async (e) =>
                      await handleCoffeeChatEmailToggle(e.target.checked)
                    }
                    disabled={coffeeChatEmailLoading}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <div className="toggle-status">
                  {coffeeChatEmailLoading && (
                    <span className="toggle-status-text">Saving…</span>
                  )}
                  {!coffeeChatEmailLoading &&
                    coffeeChatEmailStatus === 'success' && (
                      <span className="toggle-status-text is-success">
                        ✓ Saved
                      </span>
                    )}
                  {!coffeeChatEmailLoading &&
                    coffeeChatEmailStatus === 'error' && (
                      <span className="toggle-status-text is-error">
                        ✗ Failed
                      </span>
                    )}
                </div>
              </div>
            </div>

            <hr style={{ margin: '1.25rem 0', borderColor: '#DEE1EE' }} />

            <div className="test-email-section">
              <p className="label" style={{ marginBottom: '0.5rem' }}>
                Send Test Email
              </p>
              <p className="help" style={{ marginBottom: '1rem' }}>
                Send a sample coffee chat match email to verify the email
                template is working correctly.
              </p>
              <div className="field has-addons">
                <div className="control is-expanded">
                  <input
                    className="input"
                    type="email"
                    placeholder="Enter email address"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') await handleSendTestEmail();
                    }}
                    disabled={testEmailSending}
                  />
                </div>
                <div className="control">
                  <button
                    className={`button is-primary ${
                      testEmailSending ? 'is-loading' : ''
                    }`}
                    onClick={handleSendTestEmail}
                    disabled={testEmailSending || !testEmailAddress.trim()}
                  >
                    Send Test Email
                  </button>
                </div>
              </div>

              {testEmailStatus === 'success' && (
                <p className="help is-success">
                  ✓ Test email sent successfully! Check the inbox.
                </p>
              )}
              {testEmailStatus === 'error' && (
                <p className="help is-danger">✗ {testEmailError}</p>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AdminSettings;
